import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
  Stack,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  TablePagination,
  Grid,
  Paper,
  Container,
  Alert,
  Snackbar,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";

// Componentes locales/servicios
import ReclamoModal from "../components/ReclamoModal";
import ReclamosTable from "../components/ReclamosTable";
import { reclamosApi } from "../services/reclamosApi";

// =================================================================
// ===== CONSTANTES GLOBALES Y HELPERS DE UI =====
// =================================================================

const PRIORIDADES = ["BAJA", "MEDIA", "ALTA", "URGENTE"];
const EXCLUIR_ASIGNADOS_ESTADOS = new Set(["CERRADO", "MANTENIMIENTO"]);
const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

const a11yProps = (index) => ({
  id: `tab-${index}`,
  "aria-controls": `tabpanel-${index}`,
});

const TabPanel = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
  >
    {value === index && <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>}
  </div>
);

function decodeJwtUsername(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(decodeURIComponent(escape(atob(base64))));
    return (
      json.username ||
      json.user?.username ||
      json.preferred_username ||
      json.email?.split("@")[0] ||
      json.sub ||
      null
    );
  } catch {
    return null;
  }
}

function getCurrentUsername() {
  try {
    const raw =
      localStorage.getItem("profile") || sessionStorage.getItem("profile");
    if (raw) {
      const p = JSON.parse(raw);
      const u =
        p.username ||
        p.user?.username ||
        p.login?.username ||
        p.account?.username;
      if (u) return u;
    }
    const loose =
      localStorage.getItem("username") ||
      sessionStorage.getItem("username") ||
      window.__USER?.username;
    if (loose) return loose;
    const jwt =
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("access_token") ||
      sessionStorage.getItem("token");
    const fromJwt = jwt ? decodeJwtUsername(jwt) : null;
    if (fromJwt) return fromJwt;
    return null;
  } catch {
    return null;
  }
}

const colorForPrioridad = (p) =>
  p === "URGENTE" ? "error" : p === "ALTA" ? "warning" : p === "MEDIA" ? "info" : "default";

const colorForEstado = (s) => {
  switch ((s || "").toUpperCase()) {
    case "REVISION":
      return "secondary";
    case "EN_PROCESO":
      return "info";
    case "EN_ESPERA":
      return "warning";
    case "MANTENIMIENTO":
      return "warning";
    case "RESUELTO":
      return "success";
    case "REABIERTO":
      return "error";
    default:
      return "default";
  }
};

const borderForEstado = (s) => {
  switch ((s || "").toUpperCase()) {
    case "RESUELTO":
      return "success.main";
    case "EN_ESPERA":
      return "warning.main";
    case "EN_PROCESO":
      return "info.main";
    case "REVISION":
      return "secondary.main";
    case "REABIERTO":
      return "error.main";
    case "MANTENIMIENTO":
      return "warning.main";
    default:
      return "divider";
  }
};

const fmt = (dt) => {
  try {
    return new Date(dt).toLocaleString("es-BO");
  } catch {
    return "";
  }
};

// =================================================================
// ===== COMPONENTE: MyPendingCards (Tarjetas de Pendientes) =====
// =================================================================

function MyPendingCards({ rows, onOpen, onFinalize, currentUsername }) {
  if (!rows?.length) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography color="text.secondary" variant="body2">
          Sin reclamos pendientes.
        </Typography>
      </Paper>
    );
  }
  return (
    <Grid container spacing={2}>
      {rows.map((r) => {
        const estado = String(r.estado || "").toUpperCase();
        const estadoColor = colorForEstado(estado);
        const prioridadColor = colorForPrioridad(r.prioridad);
        const leftBorder = borderForEstado(estado);
        const isCreator =
          currentUsername &&
          String(r.id_usuario || "").trim().toLowerCase() ===
            currentUsername.trim().toLowerCase();
        const canFinalize =
          isCreator && (estado === "RESUELTO" || estado === "MANTENIMIENTO");

        return (
          <Grid item xs={12} key={r.id}>
            <Paper
              onClick={() => onOpen?.(r)}
              sx={{
                p: 2,
                borderLeft: 6,
                borderLeftStyle: "solid",
                borderLeftColor: leftBorder,
                cursor: "pointer",
                transition: "background-color 0.2s",
                "&:hover": { bgcolor: "action.hover" },
              }}
              elevation={1}
            >
              <Stack
                direction="row"
                alignItems="flex-start"
                spacing={1}
                justifyContent="space-between"
              >
                <Stack spacing={0.5} sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600 }}
                    noWrap
                  >
                    {r.titulo}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Código: <b>{r.codigo}</b> • Actualizado:{" "}
                    {fmt(r.actualizado_en)}
                  </Typography>
                </Stack>

                <Stack
                  direction="column"
                  spacing={0.8}
                  alignItems="flex-end"
                  sx={{ flexShrink: 0 }}
                >
                  <Chip size="small" label={r.estado} color={estadoColor} />
                  <Chip
                    size="small"
                    label={r.prioridad}
                    color={prioridadColor}
                    variant="outlined"
                  />
                </Stack>
              </Stack>

              {(estado === "RESUELTO" || estado === "MANTENIMIENTO") && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color:
                        estado === "RESUELTO"
                          ? "success.main"
                          : "warning.main",
                      mb: canFinalize ? 1 : 0,
                    }}
                  >
                    {estado === "RESUELTO"
                      ? "¡Tu reclamo fue resuelto! Pendiente de tu cierre."
                      : "Tu reclamo está en mantenimiento. Puedes finalizarlo si estás conforme."}
                  </Typography>

                  {canFinalize && (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFinalize?.(r);
                      }}
                    >
                      Finalizar Reclamo
                    </Button>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
}

// =================================================================
// ===== COMPONENTE PRINCIPAL: ReclamosPage =====
// =================================================================

export default function ReclamosPage() {
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  // Listado general (pestaña 1)
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [listError, setListError] = useState("");

  // Bandeja (pestaña 0)
  const [pendLoading, setPendLoading] = useState(false);
  const [pendRows, setPendRows] = useState([]);
  const [pendError, setPendError] = useState("");
  const [asgLoading, setAsgLoading] = useState(false);
  const [asgRows, setAsgRows] = useState([]);
  const [asgError, setAsgError] = useState("");

  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });
  const openSnack = (msg, severity = "info") => setSnack({ open: true, msg, severity });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const [currentUsername, setCurrentUsername] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);

  // Filtros listado general
  const [filtros, setFiltros] = useState({ page: 1, limit: 20 });
  const [q, setQ] = useState(filtros.search || "");

  const updateFiltros = (patch) => {
    setFiltros((f) => ({ ...f, ...patch, page: patch.page ?? 1 }));
  };

  // Reportes (pestaña 2)
  const todayStr = useMemo(
    () => new Date().toISOString().slice(0, 10),
    []
  );
  const weekAgoStr = useMemo(
    () => new Date(Date.now() - 6 * 24 * 3600 * 1000)
      .toISOString()
      .slice(0, 10),
    []
  );
  const [repDesde, setRepDesde] = useState(weekAgoStr);
  const [repHasta, setRepHasta] = useState(todayStr);
  const [repLoading, setRepLoading] = useState(false);
  const [repData, setRepData] = useState({
    resueltos_pendientes: 0,
    resueltos_finalizados: 0,
    resueltos_total: 0,
    mantenimiento: 0,
    total: 0,
  });

  // --- Efectos y Callbacks ---

  // Debounce de búsqueda
  useEffect(() => {
    const id = setTimeout(() => {
      if ((q || "") !== (filtros.search || "")) {
        updateFiltros({ search: q || undefined });
      }
    }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Cargar listado general
  const load = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const data = await reclamosApi.listar(filtros);
      const items = Array.isArray(data) ? data : data?.items ?? [];
      const totalCount = Array.isArray(data)
        ? data.length
        : data?.total ?? items.length;
      setRows(items);
      setTotal(totalCount);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setListError("No se pudo cargar el listado de reclamos.");
    } finally {
      setLoading(false);
    }
  }, [filtros]);
  useEffect(() => {
    load();
  }, [load]);

  // Cargar usuario actual
  useEffect(() => {
    (async () => {
      const uname = (getCurrentUsername() || "").trim();
      setCurrentUsername(uname);
      if (!uname) {
        setCurrentUserId(null);
        return;
      }
      try {
        const usrs = await reclamosApi.usuariosTRS({
          role: "",
          sucursal: "La Paz",
        });
        const me = (usrs || []).find(
          (u) =>
            String(u.username || "").trim().toLowerCase() ===
            uname.toLowerCase()
        );
        setCurrentUserId(me?.id ?? null);
      } catch {
        setCurrentUserId(null);
      }
    })();
  }, []);

  // Cargar Mis pendientes (creados por mí)
  const loadMisCreados = useCallback(async () => {
    if (!currentUsername) {
      setPendRows([]);
      return;
    }
    setPendLoading(true);
    setPendError("");
    try {
      const data = await reclamosApi.listar({
        id_usuario: currentUsername,
        limit: 20,
      });
      const items = Array.isArray(data) ? data : data?.items ?? [];
      const filtered = items.filter(
        (r) => String(r.estado || "").toUpperCase() !== "CERRADO"
      );
      setPendRows(filtered.slice(0, 10));
    } catch (e) {
      setPendRows([]);
      setPendError("No se pudo cargar tus reclamos pendientes.");
    } finally {
      setPendLoading(false);
    }
  }, [currentUsername]);

  // Cargar Asignados a mí (id_responsable en BD siempre guarda el ID numérico del usuario)
  const loadAsignados = useCallback(async () => {
    if (!currentUserId) {
      setAsgRows([]);
      return;
    }
    setAsgLoading(true);
    setAsgError("");
    try {
      const data = await reclamosApi.listar({ id_responsable: currentUserId, limit: 20 });
      const items = Array.isArray(data) ? data : data?.items ?? [];
      const filtered = items.filter(
        (r) =>
          !EXCLUIR_ASIGNADOS_ESTADOS.has(
            String(r.estado || "").toUpperCase()
          )
      );
      setAsgRows(filtered.slice(0, 10));
    } catch (e) {
      setAsgRows([]);
      setAsgError("No se pudo cargar los reclamos asignados a ti.");
    } finally {
      setAsgLoading(false);
    }
  }, [currentUserId]);

  // Finalizar reclamo
  const finalizeReclamo = useCallback(
    async (rec) => {
      try {
        await reclamosApi.cambiarEstado(rec.id, { estado: "CERRADO" });
        await Promise.all([loadMisCreados(), loadAsignados()]);
        openSnack("Reclamo finalizado", "success");
      } catch (e) {
        openSnack("No se pudo finalizar el reclamo.", "error");
      }
    },
    [loadMisCreados, loadAsignados]
  );

  // Cargar bandeja y reportes al cambiar de pestaña
  useEffect(() => {
    if (tab === 0) {
      loadMisCreados();
      loadAsignados();
    }
    if (tab === 2) {
      loadReportes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Re-cargar bandeja cuando resolvemos username/id
  useEffect(() => {
    if (tab === 0 && (currentUsername || currentUserId != null)) {
      loadMisCreados();
      loadAsignados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUsername, currentUserId]);

  // Cargar Reportes
  const loadReportes = useCallback(async () => {
    setRepLoading(true);
    try {
      const data = await reclamosApi.resumenEstados({
        desde: repDesde,
        hasta: repHasta,
      });
      setRepData({
        resueltos_pendientes: Number(data?.resueltos_pendientes || 0),
        resueltos_finalizados: Number(data?.resueltos_finalizados || 0),
        resueltos_total: Number(data?.resueltos_total || 0),
        mantenimiento: Number(data?.mantenimiento || 0),
        total: Number(data?.total || 0),
      });
    } catch {
      setRepData({
        resueltos_pendientes: 0,
        resueltos_finalizados: 0,
        resueltos_total: 0,
        mantenimiento: 0,
        total: 0,
      });
    } finally {
      setRepLoading(false);
    }
  }, [repDesde, repHasta]);

  const columns = useMemo(
    () => [
      { id: "codigo", label: "Código", minWidth: 80 },
      { id: "titulo", label: "Título", minWidth: 180 },
      { id: "prioridad", label: "Prioridad", minWidth: 100 },
      { id: "estado", label: "Estado", minWidth: 100 },
      { id: "actualizado_en", label: "Actualizado", minWidth: 150 },
    ],
    []
  );

  const handleChangeTab = (_, v) => setTab(v);
  const handleChangePage = (_, pageZeroBased) =>
    updateFiltros({ page: pageZeroBased + 1 });
  const handleChangeRowsPerPage = (e) =>
    updateFiltros({ limit: parseInt(e.target.value, 10), page: 1 });
  const pageZeroBased = (filtros.page || 1) - 1;

  // =================================================================
  // RENDER
  // =================================================================

  return (
    <DashboardLayout>
      <PageHeader
        title="Reclamos"
        subtitle="Seguimiento de reclamos de clientes"
        actions={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
          >
            Nuevo reclamo
          </Button>
        }
      />

      <Container maxWidth="xl" sx={{ pt: 2 }}>
        <Box sx={{ width: "100%", borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tab}
            onChange={handleChangeTab}
            aria-label="Pestañas de Reclamos"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Mi Bandeja" {...a11yProps(0)} />
            <Tab label="Listado de Reclamos" {...a11yProps(1)} />
            <Tab label="Reportes" {...a11yProps(2)} />
          </Tabs>
        </Box>

        {/* --- Pestaña 0: Mi Bandeja --- */}
        <TabPanel value={tab} index={0}>
          <Stack spacing={2.5}>
            <Box>
              <IconButton
                color="primary"
                onClick={() => {
                  loadMisCreados();
                  loadAsignados();
                }}
                disabled={pendLoading || asgLoading}
                title="Recargar bandeja"
                sx={{ border: "1px solid #ccc" }}
              >
                {pendLoading || asgLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Box>

            <Grid container spacing={3}>
              {/* Columna izquierda: Mis pendientes */}
              <Grid item size={{ xs:12, md:6}} >
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    mb={2}
                  >
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{ flex: 1, fontWeight: 700 }}
                    >
                      Mis Reclamos Pendientes ({pendRows.length})
                    </Typography>
                  </Stack>

                  {pendError && (
                    <Alert severity="error" sx={{ mb: 2 }} action={
                      <Button color="inherit" size="small" onClick={loadMisCreados}>Reintentar</Button>
                    }>
                      {pendError}
                    </Alert>
                  )}

                  <MyPendingCards
                    rows={pendRows}
                    currentUsername={currentUsername}
                    onOpen={(r) => navigate(`/reclamos/${r.id}`)}
                    onFinalize={finalizeReclamo}
                  />
                </Paper>
              </Grid>

              {/* Columna derecha: Asignados a mí */}
              <Grid item size={{xs:12, md:6}}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    mb={2}
                  >
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{ flex: 1, fontWeight: 700 }}
                    >
                      Asignados a Mi ({asgRows.length})
                    </Typography>
                  </Stack>

                  {asgError && (
                    <Alert severity="error" sx={{ mb: 2 }} action={
                      <Button color="inherit" size="small" onClick={loadAsignados}>Reintentar</Button>
                    }>
                      {asgError}
                    </Alert>
                  )}

                  <ReclamosTable
                    rows={asgRows}
                    columns={columns}
                    loading={asgLoading}
                    onRowClick={(r) => navigate(`/reclamos/${r.id}`)}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        </TabPanel>

        {/* --- Pestaña 1: Listado general --- */}
        <TabPanel value={tab} index={1}>
          <Stack spacing={3}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems="stretch"
            >
              <Box sx={{ flexGrow: 1 }} />

              <TextField
                size="small"
                label="Buscar (Título o Descripción)"
                placeholder="Buscar por título o descripción..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                sx={{ minWidth: 260 }}
              />

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="prio-label">Prioridad</InputLabel>
                <Select
                  labelId="prio-label"
                  value={filtros.prioridad || ""}
                  label="Prioridad"
                  onChange={(e) =>
                    updateFiltros({
                      prioridad: e.target.value || undefined,
                    })
                  }
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Todas las Prioridades</em>
                  </MenuItem>
                  {PRIORIDADES.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <IconButton
                onClick={load}
                disabled={loading}
                title="Recargar listado"
              >
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Stack>

            {listError && (
              <Alert severity="error" action={
                <Button color="inherit" size="small" onClick={load}>Reintentar</Button>
              }>
                {listError}
              </Alert>
            )}

            <Paper variant="outlined">
              <ReclamosTable
                rows={rows}
                columns={columns}
                loading={loading}
                onRowClick={(r) => navigate(`/reclamos/${r.id}`)}
              />

              <TablePagination
                component="div"
                count={total}
                page={pageZeroBased}
                onPageChange={handleChangePage}
                rowsPerPage={filtros.limit || 20}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${
                    count !== -1 ? count : `más de ${to}`
                  }`
                }
              />
            </Paper>
          </Stack>
        </TabPanel>

        {/* --- Pestaña 2: Reportes --- */}
        <TabPanel value={tab} index={2}>
          <Stack spacing={3}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
              Resumen de Estados por Fechas
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <TextField
                type="date"
                label="Desde"
                size="small"
                value={repDesde}
                onChange={(e) => setRepDesde(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                type="date"
                label="Hasta"
                size="small"
                value={repHasta}
                onChange={(e) => setRepHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <Button
                variant="contained"
                onClick={loadReportes}
                disabled={repLoading}
                startIcon={
                  repLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <RefreshIcon />
                  )
                }
              >
                Consultar
              </Button>
            </Stack>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} lg={3}>
                <Paper
                  elevation={3}
                  sx={{ p: 3, borderLeft: 5, borderLeftColor: "success.main" }}
                >
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                  >
                    Resueltos (Sin Finalizar)
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 600 }}>
                    {repData.resueltos_pendientes}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <Paper
                  elevation={3}
                  sx={{ p: 3, borderLeft: 5, borderLeftColor: "primary.main" }}
                >
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                  >
                    Resueltos Finalizados
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 600 }}>
                    {repData.resueltos_finalizados}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <Paper
                  elevation={3}
                  sx={{ p: 3, borderLeft: 5, borderLeftColor: "warning.main" }}
                >
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                  >
                    Mantenimiento
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 600 }}>
                    {repData.mantenimiento}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <Paper
                  elevation={3}
                  sx={{ p: 3, borderLeft: 5, borderLeftColor: "text.primary" }}
                >
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                  >
                    Total de Reclamos en Rango
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 600 }}>
                    {repData.total}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        </TabPanel>
      </Container>

      {/* Modal de creación */}
      <ReclamoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccessNavigate={(id) => {
          setModalOpen(false);
          load();
          navigate(`/reclamos/${id}`);
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled" sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
